import { useCallback, useEffect, useRef, useState } from 'react';
import { INACTIVITY_LOGOUT_MS, INACTIVITY_WARNING_MS } from '../auth/inactivityConfig';

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'input', 'scroll', 'touchstart', 'click'] as const;

/** Throttle high-frequency events (e.g. scroll) so timers are not reset constantly. */
const ACTIVITY_THROTTLE_MS = 1000;

export interface UseAutoLogoutOptions {
  enabled: boolean;
  onLogout: () => void;
}

export function useAutoLogout({ enabled, onLogout }: UseAutoLogoutOptions) {
  const [warningOpen, setWarningOpen] = useState(false);
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityThrottleRef = useRef(0);
  const onLogoutRef = useRef(onLogout);
  onLogoutRef.current = onLogout;

  const clearTimers = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
  }, []);

  const scheduleTimers = useCallback(() => {
    clearTimers();
    warningTimerRef.current = setTimeout(() => {
      setWarningOpen(true);
    }, INACTIVITY_WARNING_MS);
    logoutTimerRef.current = setTimeout(() => {
      setWarningOpen(false);
      onLogoutRef.current();
    }, INACTIVITY_LOGOUT_MS);
  }, [clearTimers]);

  const registerActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastActivityThrottleRef.current < ACTIVITY_THROTTLE_MS) return;
    lastActivityThrottleRef.current = now;
    setWarningOpen(false);
    scheduleTimers();
  }, [scheduleTimers]);

  const stayLoggedIn = useCallback(() => {
    setWarningOpen(false);
    lastActivityThrottleRef.current = Date.now();
    scheduleTimers();
  }, [scheduleTimers]);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      setWarningOpen(false);
      return;
    }

    lastActivityThrottleRef.current = Date.now();
    scheduleTimers();

    const onActivity = () => registerActivity();
    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, onActivity, { passive: true });
    }

    return () => {
      clearTimers();
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, onActivity);
      }
    };
  }, [enabled, clearTimers, scheduleTimers, registerActivity]);

  const secondsUntilLogout = Math.max(
    0,
    Math.ceil((INACTIVITY_LOGOUT_MS - INACTIVITY_WARNING_MS) / 1000),
  );

  return { warningOpen, stayLoggedIn, secondsUntilLogout };
}
